import { useState, useEffect, useCallback } from 'react'
import { gql, useLazyQuery } from '@apollo/client'
import { Metadata, ResultsList, WhereGraphql } from 'src/admin/core/models'
import _ from 'lodash'
import { AgencyWeeklySales } from '../../weeklyBalance/models/AgencyWeeklySales'

const GET_WORKSTATION_BALANCES = gql`
  query ByWorkstationTicketTransactionsSummary(
    $provider: String!
    $enterpriseId: String!
    $agencyId: String!
    $currency: CurrencyType!
    $product: ProductEnum!
    $from: DateTime
    $to: DateTime
    $take: Int
  ) {
    byWorkstationTicketTransactionsSummary(
      provider: $provider
      enterpriseId: $enterpriseId
      agencyId: $agencyId
      currency: $currency
      product: $product
      from: $from
      to: $to
      take: $take
    ) {
      items {
        amount
        commission
        surcharge
        key {
          id
          category
          date
          transactionType
          currency
          workstation {
            name
            agency {
              id
              name
            }
            enterprise {
              id
              name
            }
          }
          provider
          product
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
      totalCount
    }
  }
`

interface Data {
  byWorkstationTicketTransactionsSummary: {
    items: AgencyWeeklySales[];
    pageInfo: { hasNextPage: boolean; hasPreviousPage: boolean };
    totalCount: number;
  }
}

export const useWorkstationBalances = () => {
  const [getResults, { loading, error, data }] = useLazyQuery<Data>(GET_WORKSTATION_BALANCES, {
    fetchPolicy: 'network-only',
  });

  const [results, setResults] = useState<ResultsList<AgencyWeeklySales, Metadata>>({
    results: [],
    metadata: {},
  });
  

  const getWorkstationBalances = useCallback(
    ({
      product,
      currency,
      from,
      to,
      where,
      provider,
      enterpriseId,
    }: {
      product: string
      currency: string
      from: string
      to: string
      where?: WhereGraphql | WhereGraphql[]
      provider?: string
      enterpriseId?: string
    }) => {
      // Derive agencyId from provided where filter when possible
      let agencyId = ''
      if (where && !Array.isArray(where)) {
        const maybeKey: any = (where as any).key
        if (maybeKey?.id?.eq) {
          agencyId = maybeKey.id.eq as string
        }
      }

      getResults({
        variables: {
          product,
          currency,
          to,
          from,
          provider: provider ?? '',
          enterpriseId: enterpriseId ?? '',
          agencyId,
          take: 100,
        },
      })
    },
    [getResults],
  )

  useEffect(() => {
    if (data?.byWorkstationTicketTransactionsSummary) {
      const rawItems = data.byWorkstationTicketTransactionsSummary.items || []
      const mapped: AgencyWeeklySales[] = rawItems.map((it: any) => {
        // Clone to avoid mutating apollo cache objects
        const cloned = _.cloneDeep(it)
        const agency = cloned?.key?.workstation?.agency
        // Ensure expected shape: key.agency and agency.products (for consumer code)
        cloned.key = {
          ...cloned.key,
          agency: {
            ...(agency || {}),
            products: Array.isArray(agency?.products) ? agency.products : [],
          },
        }
        return cloned as AgencyWeeklySales
      })

      setResults({
        results: mapped,
        metadata: {
          Count: 100,
        },
      })
    }
  }, [loading])

  return {
    loading,
    error,
    results,
    getWorkstationBalances,
  }
}

